import React from 'react';
import type {
  ComponentProps,
  TableRowElement,
} from '../../../types/aha-editor';
import { isTableCellElement } from '../../../types/guards';
import { ApplicationView, useController } from '@aha-app/mvc';
import TableController from './TableController';
import { ReactEditor, useSlateStatic } from 'slate-react';

interface TableRowComponentProps extends ComponentProps<TableRowElement> {
  children: React.ReactNode;
}

const TableRowComponent: React.FC<TableRowComponentProps> = ({
  attributes,
  element,
  children,
}) => {
  const controller = useController(TableController);
  const { tableElement } = controller.state;
  const editor = useSlateStatic();
  const path = ReactEditor.findPath(editor, element);
  const rowPath = path.slice(-1)[0]; // Get the row index within the table

  const hasNumberedRows = tableElement.table_numberedRows;
  const hasHeaderRow = tableElement.table_headerRow;

  // Determine if this is the header row or a body row
  const isHeaderRow = hasHeaderRow && rowPath === 0;
  const isBodyRow = !hasHeaderRow || rowPath > 0;

  // Calculate the row number for display (1-based, excluding header row if present)
  const rowNumber = hasHeaderRow ? rowPath : rowPath + 1;

  if (element.children.some(isTableCellElement)) {
    return (
      <tr {...attributes} className="Editor__tr">
        {hasNumberedRows && isHeaderRow && (
          <th
            className="Editor__th Editor__th--row-number-header"
            contentEditable={false}
            suppressContentEditableWarning
          />
        )}
        {hasNumberedRows && isBodyRow && (
          <th
            className="Editor__th Editor__th--row-number"
            contentEditable={false}
            suppressContentEditableWarning
            scope="row"
          >
            {rowNumber}
          </th>
        )}
        {children}
      </tr>
    );
  }

  return (
    <tr {...attributes} className="Editor__tr">
      <td style={{ display: 'none' }}>{children}</td>
    </tr>
  );
};

export default ApplicationView(TableRowComponent);
